import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { m } from 'framer-motion';
import { FiChevronDown, FiMenu, FiX, FiCode } from 'react-icons/fi';
import { internalUrl } from './siteConfig';
import { capturePathBeforeHomeNavigation } from './contactSourceStorage';

const TOOL_LINKS = [
  { name: 'Free SEO audit', path: '/check-your-seo' },
  { name: 'Accessibility scan', path: '/accessibility-check' },
  { name: 'Website tax calculator', path: '/website-tax-calculator' },
  { name: 'Scope fit wizard', path: '/scope-estimator' },
  { name: 'Reading time helper', path: '/reading-time-tool' },
  { name: 'Competitor tracker', path: '/competitor-tracker' },
  { name: 'SERP overview', path: '/serp-overview' },
  { name: 'Keywords explorer', path: '/keywords-explorer' },
  { name: 'Website crawler', path: '/website-crawler' },
  { name: 'Backlink checker', path: '/backlink-checker' }
];

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [toolsOpen, setToolsOpen] = useState(false);
  const [mobileToolsOpen, setMobileToolsOpen] = useState(false);
  const toolsWrapRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isMenuOpen) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isMenuOpen]);

  useEffect(() => {
    if (!isMenuOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isMenuOpen]);

  useEffect(() => {
    if (!toolsOpen) return undefined;
    const onDoc = (e) => {
      const el = toolsWrapRef.current;
      if (el && !el.contains(e.target)) setToolsOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setToolsOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [toolsOpen]);

  useEffect(() => {
    if (!isMenuOpen) setMobileToolsOpen(false);
  }, [isMenuOpen]);

  const navItems = [
    { name: 'Home', id: 'home' },
    { name: 'Services', id: 'services' },
    { name: 'Portfolio', id: 'portfolio' },
    { name: 'About', id: 'about' },
    { name: 'FAQ', id: 'faq' },
    { name: 'Areas', id: 'locations' },
    { name: 'Guides', id: 'guides' },
    { name: 'Contact', id: 'contact' }
  ];

  const handleNavClick = (sectionId) => {
    const id = String(sectionId).replace(/^#/, '');
    setIsMenuOpen(false);

    if (id === 'services') {
      if (location.pathname === '/services') {
        document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' });
      } else {
        navigate('/services');
      }
      return;
    }

    if (id === 'locations') {
      navigate('/locations');
      return;
    }

    if (id === 'guides') {
      navigate('/guides');
      return;
    }

    if (id === 'tax-calculator') {
      navigate('/website-tax-calculator');
      return;
    }

    const isHomeShell = ['/', '/faq', '/services'].includes(location.pathname);

    if (!isHomeShell) {
      if (id === 'faq') {
        navigate('/faq');
        window.setTimeout(() => {
          document.getElementById('faq')?.scrollIntoView({ behavior: 'smooth' });
        }, 150);
        return;
      }
      if (id === 'home') {
        navigate('/');
        window.setTimeout(() => window.scrollTo(0, 0), 0);
        return;
      }
      capturePathBeforeHomeNavigation();
      navigate('/');
      window.setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
      }, 120);
      return;
    }

    if (id === 'faq') {
      if (location.pathname === '/faq') {
        document.getElementById('faq')?.scrollIntoView({ behavior: 'smooth' });
      } else {
        navigate('/faq');
      }
      return;
    }

    if (id === 'home' && location.pathname === '/faq') {
      navigate('/');
      window.setTimeout(() => {
        document.getElementById('home')?.scrollIntoView({ behavior: 'smooth' });
      }, 80);
      return;
    }

    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  const headerBarClasses = [
    'fixed top-0 left-0 right-0 z-[100] transition-all duration-300',
    'max-lg:bg-white max-lg:shadow-sm max-lg:border-b max-lg:border-gray-100',
    isScrolled ? 'lg:bg-white/90 lg:backdrop-blur-md lg:shadow-lg' : 'lg:bg-transparent'
  ].join(' ');

  return (
    <>
      <m.header
        className={headerBarClasses}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6 }}
        role="banner"
        aria-label="Main navigation"
      >
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16 lg:h-20">
            <m.div
              className="flex items-center space-x-3"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <div
                className="w-12 h-12 bg-slate-900 rounded-xl flex items-center justify-center shadow-md ring-1 ring-slate-900/10"
                aria-hidden="true"
              >
                <FiCode className="text-white text-2xl" />
              </div>
              <div className="flex flex-col min-w-0">
                <span className="text-xl sm:text-2xl font-bold font-display text-slate-900 leading-tight truncate">
                  WebCodeCraft
                </span>
                <span className="text-xs text-slate-600 font-medium">Web Development</span>
              </div>
            </m.div>

            <nav className="hidden lg:flex items-center space-x-8" role="navigation" aria-label="Main menu">
              {navItems.map((item, index) => (
                <m.a
                  key={item.name}
                  href={internalUrl(item.id)}
                  className="text-slate-900 hover:text-slate-950 font-medium transition-colors duration-200 relative group focus:outline-none focus:ring-2 focus:ring-slate-900 focus:ring-offset-2 rounded"
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ y: -2 }}
                  onClick={(e) => {
                    e.preventDefault();
                    handleNavClick(item.id);
                  }}
                  aria-label={item.id === 'locations' ? 'Areas we serve in Devon, Cornwall and UK cities' : `Navigate to ${item.name} section`}
                >
                  {item.name}
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-slate-900 transition-all duration-300 group-hover:w-full" />
                </m.a>
              ))}

              <div className="relative" ref={toolsWrapRef}>
                <button
                  type="button"
                  id="nav-tools-button"
                  className="inline-flex items-center gap-1 text-slate-900 hover:text-slate-950 font-medium transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-slate-900 focus:ring-offset-2 rounded px-1 py-0.5"
                  aria-expanded={toolsOpen}
                  aria-haspopup="true"
                  aria-controls="nav-tools-dropdown"
                  onClick={() => setToolsOpen((v) => !v)}
                >
                  Tools
                  <FiChevronDown
                    className={`transition-transform ${toolsOpen ? 'rotate-180' : ''}`}
                    aria-hidden="true"
                    size={18}
                  />
                </button>
                {toolsOpen && (
                  <div
                    id="nav-tools-dropdown"
                    role="menu"
                    aria-labelledby="nav-tools-button"
                    className="absolute right-0 top-full mt-2 w-64 max-h-[min(70vh,22rem)] overflow-y-auto rounded-xl bg-white shadow-lg border border-slate-200 py-1 z-[110]"
                  >
                    {TOOL_LINKS.map((t) => (
                      <Link
                        key={t.path}
                        to={t.path}
                        role="menuitem"
                        className="block px-4 py-2.5 text-sm text-slate-800 hover:bg-slate-50 focus:outline-none focus-visible:bg-slate-100"
                        onClick={() => setToolsOpen(false)}
                      >
                        {t.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              <m.button
                className="btn btn-primary focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => handleNavClick('contact')}
                aria-label="Get started with your project"
              >
                Get Started
              </m.button>
            </nav>

            <m.button
              type="button"
                className="lg:hidden p-2.5 rounded-lg hover:bg-slate-100 transition-colors focus:outline-none focus:ring-2 focus:ring-slate-900 text-slate-900"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              whileTap={{ scale: 0.95 }}
              aria-expanded={isMenuOpen}
              aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
              aria-controls="mobile-menu"
            >
              {isMenuOpen ? <FiX className="text-2xl" /> : <FiMenu className="text-2xl" />}
            </m.button>
          </div>
        </div>
      </m.header>

      {isMenuOpen && (
        <>
          <button
            type="button"
            className="fixed inset-0 top-16 z-[90] bg-black/50 lg:hidden cursor-default border-0 p-0 m-0"
            aria-label="Close menu"
            onClick={() => setIsMenuOpen(false)}
          />
          <nav
            id="mobile-menu"
            className="fixed left-0 right-0 top-16 z-[100] max-h-[calc(100dvh-4rem)] overflow-y-auto overscroll-contain bg-white shadow-xl border-b border-gray-200 lg:hidden"
            role="navigation"
            aria-label="Mobile menu"
          >
            <div className="container mx-auto px-4 py-4 pb-8 space-y-1">
              {navItems.map((item) => (
                <a
                  key={item.name}
                  href={internalUrl(item.id)}
                  className="block w-full text-left text-slate-900 hover:bg-slate-100 font-medium transition-colors rounded-lg px-3 py-3 focus:outline-none focus:ring-2 focus:ring-slate-900"
                  onClick={(e) => {
                    e.preventDefault();
                    handleNavClick(item.id);
                  }}
                >
                  {item.name}
                </a>
              ))}
              <div className="border-t border-slate-100 pt-2 mt-1">
                <button
                  type="button"
                  className="flex w-full items-center justify-between text-left text-slate-900 hover:bg-slate-100 font-medium rounded-lg px-3 py-3 focus:outline-none focus:ring-2 focus:ring-slate-900"
                  aria-expanded={mobileToolsOpen}
                  aria-controls="mobile-tools-list"
                  id="mobile-tools-button"
                  onClick={() => setMobileToolsOpen((v) => !v)}
                >
                  Tools
                  <FiChevronDown
                    className={`shrink-0 transition-transform ${mobileToolsOpen ? 'rotate-180' : ''}`}
                    aria-hidden="true"
                  />
                </button>
                {mobileToolsOpen && (
                  <div id="mobile-tools-list" className="pl-3 ml-3 border-l-2 border-slate-200 space-y-1 pb-2" role="group" aria-label="Tools">
                    {TOOL_LINKS.map((t) => (
                      <Link
                        key={t.path}
                        to={t.path}
                        className="block py-2.5 px-2 text-slate-800 hover:bg-slate-100 rounded-lg text-sm font-medium"
                        onClick={() => {
                          setIsMenuOpen(false);
                          setMobileToolsOpen(false);
                        }}
                      >
                        {t.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
              <button
                type="button"
                className="btn btn-primary w-full mt-4 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2"
                onClick={() => handleNavClick('contact')}
              >
                Get Started
              </button>
            </div>
          </nav>
        </>
      )}
    </>
  );
};

export default Header;
